import React, { useState, useEffect, useRef } from 'react';
import { getStatusStyle } from '@data/statusConfig';
import { saveOrderEdit, setPersonalOrderImageEffect } from '@api';
import { FRAMES as WALL_FRAMES } from '@pages/WallProduct/data';
import ShippingTracker from './ShippingTracker';
import ReplaceImageModal from './ReplaceImageModal';

const FRAME_LABELS = {
  classic: 'קלאסי',
  bold: 'נועז',
  ever: 'נקי',
  clean: 'אותנטי',
  village: 'כפרי',
  nordic: 'נורדי',
};

const FRAME_MAP = {
  classic: { bg: '/assets/file/images/frame_classic.png', icon: '/assets/file/images/d1.png', left: '16.5%', top: '16.2%', width: '65.4%', height: '66.5%' },
  bold:    { bg: '/assets/file/images/frame_bold.png',    icon: '/assets/file/images/d2.png', left: '3.8%',  top: '3.8%',  width: '90.4%', height: '91%' },
  ever:    { bg: '/assets/file/images/frame_even.png',    icon: '/assets/file/images/d3.png', left: '3.5%',  top: '3.5%',  width: '90.8%', height: '91%' },
  clean:   { bg: '/assets/file/images/frame_clean.png',   icon: '/assets/file/images/d4.png', left: '13.8%', top: '14.2%', width: '70.4%', height: '70%' },
  nordic:  { bg: '/assets/file/images/frame_nordic.png',  icon: '/assets/file/images/d6.png', left: '3.8%',  top: '3.8%',  width: '90.4%', height: '91%' },
  village: { bg: '/assets/file/images/frame_village.png', icon: '/assets/file/images/d5.png', left: '16.5%', top: '16.2%', width: '65.4%', height: '66.5%' },
};


const OrderDetail = ({
  orderId,
  token,
  fetchOrderDetail,
  onBack,
}) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit flow (only while status is "נקלט"): tapping a photo selects it and
  // raises a frosted bottom editbar (like the wall page's edit menu) with
  // replace-image + frame-for-all actions. Replace: pick a file, crop it in
  // the modal, then reload the order so the new print file shows.
  const fileInputRef = useRef(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [frameSheetOpen, setFrameSheetOpen] = useState(false);
  const [frameSaving, setFrameSaving] = useState(false);
  const [frameError, setFrameError] = useState('');
  // Pending (unconfirmed) frame choice — previewed locally on all photos;
  // saved to the server once, on סיום. null = no pending change.
  const [pendingFrame, setPendingFrame] = useState(null);
  const [effectSheetOpen, setEffectSheetOpen] = useState(false);
  const [effectSaving, setEffectSaving] = useState(false);
  const [effectError, setEffectError] = useState('');
  // Pending (unconfirmed) B&W choice — previewed with CSS only; the server
  // bake happens once, on סיום. null = no pending change.
  const [pendingBw, setPendingBw] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacedNotice, setReplacedNotice] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        const result = await fetchOrderDetail(token, orderId);
        if (result.success) {
          setOrder(result.order);
        } else {
          setError('לא ניתן לטעון את פרטי ההזמנה');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'שגיאה בטעינת ההזמנה');
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderId, token, fetchOrderDetail]);

  useEffect(() => {
    if (!replacedNotice) return undefined;
    const timer = setTimeout(() => setReplacedNotice(false), 5000);
    return () => clearTimeout(timer);
  }, [replacedNotice]);

  // Click anywhere outside the snackbar / a photo closes the edit snackbar.
  useEffect(() => {
    if (selectedIdx === null) return undefined;
    const onPointerDown = (e) => {
      if (
        e.target.closest('.pa-editbar') ||
        e.target.closest('.pa-fsheet') ||
        e.target.closest('.pa-detail__framed--selectable') ||
        e.target.closest('.pa-replace__overlay')
      ) {
        return;
      }
      setSelectedIdx(null);
      setFrameSheetOpen(false);
      setFrameError('');
      setPendingFrame(null);
      setEffectSheetOpen(false);
      setEffectError('');
      setPendingBw(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [selectedIdx]);

  const toggleSelect = (i) => {
    setFrameSheetOpen(false);
    setFrameError('');
    setPendingFrame(null);
    setEffectSheetOpen(false);
    setEffectError('');
    setPendingBw(null);
    setSelectedIdx((cur) => (cur === i ? null : i));
  };

  const closeEditbar = () => {
    setSelectedIdx(null);
    setFrameSheetOpen(false);
    setFrameError('');
    setPendingFrame(null);
    setEffectSheetOpen(false);
    setEffectError('');
    setPendingBw(null);
  };

  const startReplace = (img) => {
    if (!img) return;
    setReplaceTarget({ imageName: img.imageName, imageId: img.imageId });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFilePicked = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setReplaceFile(file);
  };

  const closeReplaceModal = () => {
    setReplaceFile(null);
    setReplaceTarget(null);
  };

  // Swap ONLY the replaced photo in place. A refetch would flash the loading
  // skeleton and re-sign every S3 URL (reloading the whole grid), so instead
  // we show the exact crop the user just saved; bw is applied via CSS.
  const handleReplaced = ({ dataUrl, bwFilter } = {}) => {
    const target = replaceTarget;
    closeReplaceModal();
    closeEditbar();
    setReplacedNotice(true);
    if (!dataUrl || !target) return;
    setOrder((o) => ({
      ...o,
      images: (o.images || []).map((img) => {
        const isTarget =
          (target.imageName && img.imageName === target.imageName) ||
          (target.imageId && img.imageId === target.imageId);
        return isTarget ? { ...img, url: dataUrl, localBw: !!bwFilter } : img;
      }),
    }));
  };

  // Frame change applies to the WHOLE order (frameType is per-order).
  // Card taps preview locally (pendingFrame); the server save happens ONCE,
  // on the sheet's confirm button. Dismissing discards the pending choice.
  const commitFrame = async () => {
    if (frameSaving) return;
    if (!order || !pendingFrame || pendingFrame === order.frameType) {
      setFrameSheetOpen(false);
      setPendingFrame(null);
      setFrameError('');
      return;
    }
    setFrameSaving(true);
    setFrameError('');
    try {
      const result = await saveOrderEdit(token, orderId, { frameType: pendingFrame });
      if (!result.success) {
        throw new Error(result.error || 'שגיאה בעדכון המסגרת');
      }
      setOrder((o) => ({ ...o, frameType: pendingFrame }));
      setFrameSheetOpen(false);
      setPendingFrame(null);
    } catch (err) {
      setFrameError(err.response?.data?.error || err.message || 'שגיאה בעדכון המסגרת');
    } finally {
      setFrameSaving(false);
    }
  };

  const committedBw = (img) =>
    img ? (img.localBw !== undefined ? img.localBw : !!img.bwFilter) : false;

  // Confirm (סיום) — the ONLY place the server bakes/restores the print
  // file. Card taps before this are pure CSS preview (pendingBw).
  const commitEffect = async () => {
    const img = order?.images?.[selectedIdx];
    if (effectSaving) return;
    if (!img || pendingBw === null || pendingBw === committedBw(img)) {
      setEffectSheetOpen(false);
      setPendingBw(null);
      setEffectError('');
      return;
    }
    setEffectSaving(true);
    setEffectError('');
    try {
      const result = await setPersonalOrderImageEffect(token, orderId, {
        imageName: img.imageName,
        imageId: img.imageId,
        bw: pendingBw,
      });
      if (!result.success) {
        throw new Error(result.error || 'שגיאה בעדכון האפקט');
      }
      setOrder((o) => ({
        ...o,
        images: o.images.map((x, i) =>
          i === selectedIdx
            ? {
                ...x,
                url: result.url || x.url,
                originalUrl: result.originalUrl || x.originalUrl,
                bwFilter: pendingBw,
                localBw: undefined,
              }
            : x
        ),
      }));
      setEffectSheetOpen(false);
      setPendingBw(null);
    } catch (err) {
      setEffectError(err.response?.data?.error || err.message || 'שגיאה בעדכון האפקט');
    } finally {
      setEffectSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pa-detail">
        <button type="button" className="pa-detail__back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
          חזרה להזמנות
        </button>

        {/* Shipping tracker skeleton — reserves space so the page doesn't
            shift when the real 5-step stepper renders. */}
        <div className="pa-detail__tracker pa-detail--skeleton" aria-hidden="true">
          <div className="pa-tracker__header">
            <div className="skeleton-line" style={{ width: 120, height: 13 }} />
            <div className="skeleton-line" style={{ width: 150, height: 26 }} />
          </div>
          <ol className="pa-tracker__steps">
            <span className="pa-tracker__rail" />
            {[1, 2, 3, 4, 5].map((i) => (
              <li key={i} className="pa-tracker__step">
                <span className="pa-tracker__circle pa-tracker__circle--skel" />
                <div className="skeleton-line" style={{ width: '70%', height: 11, marginTop: 6 }} />
              </li>
            ))}
          </ol>
        </div>

        {/* Header skeleton */}
        <div className="pa-detail__header pa-detail--skeleton">
          <div className="pa-detail__header-info">
            <div className="skeleton-line" style={{ width: 170, maxWidth: '60%', height: 18 }} />
            <div className="skeleton-line" style={{ width: 180, height: 13, marginTop: 6 }} />
          </div>
          <div className="skeleton-line" style={{ width: 80, height: 28, borderRadius: 999 }} />
        </div>

        {/* Body — gallery + sidebar, matching the real layout */}
        <div className="pa-detail__body">
          <div className="pa-detail__main">
            <div className="pa-detail__section">
              <div className="skeleton-line" style={{ width: 200, height: 18, marginBottom: 14 }} />
              {/* Reuse the real .pa-detail__images--framed container so each
                  skeleton frame matches the actual photo grid dimensions
                  (3-col on desktop, 132px horizontal-scroll on mobile). */}
              <div className="pa-detail__images pa-detail__images--framed">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="pa-detail__framed">
                    <div className="pa-detail__skel-frame" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pa-detail__sidebar">
            <div className="pa-detail__section">
              <div className="skeleton-line" style={{ width: 120, height: 16, marginBottom: 14 }} />
              <div className="pa-detail__info-grid">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="pa-detail__info-item">
                    <div className="skeleton-line" style={{ width: '55%', height: 11, marginBottom: 6 }} />
                    <div className="skeleton-line" style={{ width: '75%', height: 16 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="pa-detail">
        <button type="button" className="pa-detail__back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
          חזרה להזמנות
        </button>
        <div className="pa-orders__empty">
          <p>{error || 'הזמנה לא נמצאה'}</p>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusStyle(order.statusTone);
  const addr = order.shippingAddress || {};
  // Pending frame previews on every photo instantly; committed on סיום only.
  const displayFrameType = pendingFrame || order.frameType;
  const frame = FRAME_MAP[displayFrameType];
  const frameLabel = FRAME_LABELS[displayFrameType] || displayFrameType;
  const imageCount = order.images?.length || order.items || 0;

  // Display source: prefer the COLOUR original when we have one — B&W then
  // renders via CSS, which is what makes the effect preview/restore instant.
  const photoSrc = (img) =>
    img.originalUrl || img.url || img.cropImageUrl || img.viewImageUrl || img.imageUrl;
  const displayBw = (img, i) =>
    i === selectedIdx && pendingBw !== null ? pendingBw : committedBw(img);

  const selImg = selectedIdx !== null ? order.images?.[selectedIdx] : null;
  const selBw = selImg ? (pendingBw !== null ? pendingBw : committedBw(selImg)) : false;
  const selSrc = selImg ? photoSrc(selImg) : null;

  return (
    <div className="pa-detail">
      <button type="button" className="pa-detail__back" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
        חזרה להזמנות
      </button>

      {/* Shipping Tracker (renders only when timeline data is available) */}
      {order.shippingTimeline && (
        <ShippingTracker
          timeline={order.shippingTimeline}
          expectedDeliveryDate={order.expectedDeliveryDate}
          trackingNumber={order.trackingNumber}
        />
      )}

      {/* Order Header */}
      <div className="pa-detail__header">
        <div className="pa-detail__header-info">
          <h2 className="pa-detail__title">
            הזמנה <em>#{order.orderId}</em>
          </h2>
          <span className="pa-detail__date">בוצעה בתאריך {order.date}</span>
        </div>
        <span
          className="pa-order-card__status"
          style={{
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            textShadow: statusStyle.textShadow || 'none',
          }}
        >
          {order.displayStatus || order.status}
        </span>
      </div>

      {/* Off-track statuses have no tracker — the reassurance line ("we'll
          be in touch") is all the customer gets, so it must actually render */}
      {!order.shippingTimeline && order.statusHint && (
        <p className="pa-detail__status-hint">{order.statusHint}</p>
      )}

      {/* ── Body: gallery (main) + sidebar ── */}
      <div className="pa-detail__body">
        {/* Image Gallery — main column */}
        <div className="pa-detail__main">
          {order.images && order.images.length > 0 && (
            <div className="pa-detail__section">
              <h3 className="pa-detail__section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                התמונות שבהזמנה ({imageCount})
              </h3>
              {order.canEdit && (
                <p className="pa-detail__replace-hint">
                  ההזמנה עדיין לא נשלחה להדפסה — לחצו על תמונה כדי לערוך
                </p>
              )}
              <div className="pa-detail__images pa-detail__images--framed">
                {order.images.map((img, i) => (
                  <div
                    key={img.imageId || img.imageName || i}
                    className={`pa-detail__framed${order.canEdit ? ' pa-detail__framed--selectable' : ''}${selectedIdx === i ? ' pa-detail__framed--selected' : ''}`}
                    onClick={order.canEdit ? () => toggleSelect(i) : undefined}
                    role={order.canEdit ? 'button' : undefined}
                    tabIndex={order.canEdit ? 0 : undefined}
                    onKeyDown={order.canEdit ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSelect(i);
                      }
                    } : undefined}
                  >
                    {frame ? (
                      <div
                        className="pa-detail__frame-wrap"
                        style={{ backgroundImage: `url(${frame.bg})` }}
                      >
                        <img
                          src={photoSrc(img)}
                          alt={`תמונה ${i + 1}`}
                          loading="lazy"
                          className="pa-detail__frame-photo"
                          style={{
                            left: frame.left,
                            top: frame.top,
                            width: frame.width,
                            height: frame.height,
                            filter: displayBw(img, i) ? 'grayscale(1)' : undefined,
                          }}
                        />
                      </div>
                    ) : (
                      <div className="pa-detail__image">
                        <img
                          src={photoSrc(img)}
                          alt={`תמונה ${i + 1}`}
                          loading="lazy"
                          style={displayBw(img, i) ? { filter: 'grayscale(1)' } : undefined}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar — order info + address + credits + support */}
        <div className="pa-detail__sidebar">
          {/* Order Summary */}
          <div className="pa-detail__section">
            <h3 className="pa-detail__section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              סיכום הזמנה
            </h3>
            <div className="pa-detail__info-grid">
              <div className="pa-detail__info-item">
                <span className="pa-detail__info-label">מסגרת</span>
                <span className="pa-detail__info-value pa-detail__info-value--frame">
                  {frame?.icon && (
                    <img src={frame.icon} alt="" className="pa-detail__frame-icon" />
                  )}
                  {frameLabel}
                </span>
              </div>
              <div className="pa-detail__info-item">
                <span className="pa-detail__info-label">תמונות</span>
                <span className="pa-detail__info-value">{imageCount}</span>
              </div>
              {order.price && (
                <div className="pa-detail__info-item">
                  <span className="pa-detail__info-label">סה״כ ששולם</span>
                  <span className="pa-detail__info-value">&#8362;{order.price}</span>
                </div>
              )}
              {order.trackingNumber && (
                <div className="pa-detail__info-item">
                  <span className="pa-detail__info-label">מספר מעקב</span>
                  <span className="pa-detail__info-value" dir="ltr">{order.trackingNumber}</span>
                </div>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          {(addr.fullName || addr.address || addr.city) && (
            <div className="pa-detail__section">
              <h3 className="pa-detail__section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                כתובת משלוח
              </h3>
              <div className="pa-detail__address">
                {addr.fullName && <p className="pa-detail__address-name">{addr.fullName}</p>}
                {addr.address && (
                  <p>{addr.address}{addr.addressDetails ? `, ${addr.addressDetails}` : ''}</p>
                )}
                {addr.city && <p>{addr.city}{addr.zipCode ? ` ${addr.zipCode}` : ''}</p>}
                {addr.phoneNumber && <p dir="ltr" style={{ textAlign: 'right' }}>{addr.phoneNumber}</p>}
                {addr.email && <p>{addr.email}</p>}
              </div>
            </div>
          )}

          {/* Credits earned stamp */}
          {order.creditsEarned > 0 && (
            <div className="pa-detail__credit-stamp">
              <div className="pa-detail__credit-stamp__label">Moments+ נצברו</div>
              <div className="pa-detail__credit-stamp__amount">
                {order.creditsEarned.toFixed(2)}<span className="pa-cur">&#8362;</span>
              </div>
              <div className="pa-detail__credit-stamp__sub">צבירה זמינה למימוש</div>
            </div>
          )}
        </div>
      </div>

      {/* Floating edit menu — frosted circles rising from the bottom, same
          look as the wall page's .wallp-editbar. Appears when a photo is
          selected while the order is still editable. */}
      {/* Dark scrim behind the edit tools — same shade as the wall's sheet
          overlay; the selected photo pops above it. */}
      {order.canEdit && (
        <div
          className={`pa-editbar-ov${selectedIdx !== null ? ' open' : ''}`}
          onClick={closeEditbar}
        />
      )}

      {order.canEdit && selectedIdx !== null && order.images?.[selectedIdx] && (
        <div className="pa-editbar" dir="rtl">
          <div className="pa-editbar__bar">
            <button
              type="button"
              className="pa-editbar__tool"
              onClick={() => startReplace(order.images[selectedIdx])}
            >
              <svg viewBox="0 0 24 24">
                <path d="M4 12a8 8 0 0113.6-5.6L20 8M20 12a8 8 0 01-13.6 5.6L4 16" />
                <path d="M20 4v4h-4M4 20v-4h4" />
              </svg>
              <span>החלפת תמונה</span>
            </button>
            <button
              type="button"
              className={`pa-editbar__tool${effectSheetOpen ? ' on' : ''}`}
              onClick={() => {
                setFrameSheetOpen(false);
                setEffectError('');
                setPendingBw(null);
                setEffectSheetOpen((v) => !v);
              }}
            >
              <svg viewBox="0 0 24 24" className="fill">
                <path d="M12 2.6l1.7 4.4 4.4 1.7-4.4 1.7L12 14.8l-1.7-4.4L5.9 8.7l4.4-1.7z" />
                <circle cx="18.5" cy="4.5" r="1.4" />
                <circle cx="5.5" cy="16" r="1.1" />
              </svg>
              <span>אפקט</span>
            </button>
            <button
              type="button"
              className={`pa-editbar__tool${frameSheetOpen ? ' on' : ''}`}
              onClick={() => {
                setEffectSheetOpen(false);
                setFrameError('');
                setPendingFrame(null);
                setFrameSheetOpen((v) => !v);
              }}
            >
              <svg viewBox="0 0 24 24" className="fill">
                <path fillRule="evenodd" clipRule="evenodd" d="M5 3.5h14a2 2 0 012 2v13a2 2 0 01-2 2H5a2 2 0 01-2-2v-13a2 2 0 012-2zm2 4a1 1 0 00-1 1v8a1 1 0 001 1h10a1 1 0 001-1v-8a1 1 0 00-1-1H7z" />
              </svg>
              <span>מסגרות</span>
            </button>
          </div>
        </div>
      )}

      {/* Frame sheet — same look as the wall's בחירת מסגרת bottom sheet.
          Applies to the WHOLE order (frameType is per-order). */}
      {order.canEdit && selectedIdx !== null && frameSheetOpen && (
        <section className="pa-fsheet" dir="rtl">
          <div className="pa-fsheet__head">
            <b>בחירת מסגרת</b>
            <button
              type="button"
              className="pa-fsheet__done"
              onClick={commitFrame}
              disabled={frameSaving}
            >
              {frameSaving ? 'שומר...' : 'סיום'}
            </button>
          </div>
          <div className="pa-fsheet__cards">
            {WALL_FRAMES.map((f) => (
              <div
                key={f.name}
                className={`pa-fsheet__card${displayFrameType === f.name ? ' sel' : ''}${frameSaving ? ' busy' : ''}`}
                onClick={() => { setPendingFrame(f.name); setFrameError(''); }}
              >
                {f.badge ? (
                  <span className={`pa-fsheet__badge ${f.badge.cls}`}>{f.badge.txt}</span>
                ) : null}
                <img src={`/assets/file/images/${f.img}`} alt={f.text} loading="lazy" />
                <p>{f.text}</p>
              </div>
            ))}
          </div>
          {frameError && <p className="pa-fsheet__error">{frameError}</p>}
        </section>
      )}

      {/* Effect sheet — same look as the wall's בחירת אפקט sheet.
          Applies to the SELECTED photo only. */}
      {order.canEdit && selectedIdx !== null && effectSheetOpen && selImg && (
        <section className="pa-fsheet" dir="rtl">
          <div className="pa-fsheet__head">
            <b>בחירת אפקט</b>
            <button
              type="button"
              className="pa-fsheet__done"
              onClick={commitEffect}
              disabled={effectSaving}
            >
              {effectSaving ? 'שומר...' : 'סיום'}
            </button>
          </div>
          <div className="pa-fsheet__cards">
            <button
              type="button"
              className={`pa-fsheet__fxcard${!selBw ? ' sel' : ''}${effectSaving ? ' busy' : ''}`}
              onClick={() => { setPendingBw(false); setEffectError(''); }}
            >
              <img className="pa-fsheet__fxprev" src={selSrc} alt="" />
              <span>מקורי</span>
            </button>
            <button
              type="button"
              className={`pa-fsheet__fxcard${selBw ? ' sel' : ''}${effectSaving ? ' busy' : ''}`}
              onClick={() => { setPendingBw(true); setEffectError(''); }}
            >
              <img className="pa-fsheet__fxprev bw" src={selSrc} alt="" />
              <span>שחור לבן</span>
            </button>
          </div>
          {effectError && <p className="pa-fsheet__error">{effectError}</p>}
        </section>
      )}

      {/* Success toast — wall-style green pill overlaying near the bottom */}
      {replacedNotice && (
        <div className="pa-toast">התמונה הוחלפה בהצלחה!</div>
      )}

      {/* Replace-image flow (only reachable while the order is editable).
          accept excludes HEIC on purpose — iOS transcodes to JPEG for us. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFilePicked}
      />
      {replaceFile && replaceTarget && (
        <ReplaceImageModal
          file={replaceFile}
          target={replaceTarget}
          frame={frame}
          orderId={orderId}
          token={token}
          onClose={closeReplaceModal}
          onReplaced={handleReplaced}
        />
      )}
    </div>
  );
};

export default OrderDetail;
